import { CampaignTypeEnum, EntityName, MessageTypeEnum, SystemNumberTypeEnum } from '@gr/shared/enums';
import { SystemNumberStatusEnum } from '@gr/shared/enums/domain/system-numbers/system-number-status-enum';
import { Auth0Role, DropdownEntityType, FilterDataTypeEnum, ISearchRequest, SearchSortDirectionEnum } from '@gr/shared/models';
import * as yup from 'yup';
import { IDropdownValue } from '../shared/Form/Dropdown';
import { dropdownSchema } from '../shared/Schema/yup';
import { IFilterDropdownOptions } from '../shared/Table/FilterForm';
import { IColumn, IColumnClickConfig } from '../shared/Table/types';

export const defaultSystemNumberTableOptions: ISearchRequest = {
  entity: EntityName.ALL_SYSTEM_NUMBERS_VIEW,
  filters: [],
  pagination: {
    skip: 0,
    take: 25,
  },
  sort: {
    fieldName: 'createdAt',
    sortDirection: SearchSortDirectionEnum.DESC,
  },
};

export const getColumns = (columnClickEvents: IColumnClickConfig, columnRenders: any): IColumn[] => {
  return [
    {
      headerName: 'Phone Number',
      fieldName: 'phoneNumber',
      isRowHeader: true,
      fieldType: FilterDataTypeEnum.NUMBER,
      onColumnClick: columnClickEvents['phoneNumber'],
      renderColumn: columnRenders['phoneNumber'],
    },
    {
      headerName: 'TCR Campaign Id',
      fieldName: 'tcrCampaignId',
      fieldType: FilterDataTypeEnum.STRING
    },
    {
      headerName: 'Type',
      fieldName: 'type',
      fieldType: FilterDataTypeEnum.ENUM,
    },
    {
      headerName: 'Client',
      fieldName: 'clientName',
      fieldType: FilterDataTypeEnum.TYPEAHEAD,
      dropdownType: DropdownEntityType.CLIENT
    },
    {
      headerName: 'Provider',
      fieldName: 'providerName',
      fieldType: FilterDataTypeEnum.STRING,
    },
    {
      headerName: 'Campaign Type',
      fieldName: 'campaignType',
      fieldType: FilterDataTypeEnum.ENUM,
    },
    {
      headerName: 'Message Type',
      fieldName: 'messageType',
      fieldType: FilterDataTypeEnum.ENUM,
    },
    {
      headerName: 'Status',
      fieldName: 'status',
      fieldType: FilterDataTypeEnum.ENUM,
    },
    {
      headerName: 'Is Active',
      fieldName: 'isActive',
      fieldType: FilterDataTypeEnum.BOOLEAN,
    },
  ];
};

export interface ISystemNumberForm {
  phoneNumber?: number;
  providerId: string;
  client: IDropdownValue;
  campaignType: CampaignTypeEnum;
  tcrCampaignId?: string;
}

export const systemNumberFormSchema: yup.SchemaOf<ISystemNumberForm> = yup.object().shape({
  phoneNumber: yup
    .number()
    .test('len', 'Number must be 10 digits long', (num) => num?.toString().length === 10)
    .defined('Required')
    .typeError('Required'),
  providerId: yup.string().defined('Required'),
  client: dropdownSchema,
  campaignType: yup.mixed<CampaignTypeEnum>().defined('Required'),
  tcrCampaignId: yup.string()
});

const skipFormattingEnumValues = ['GOTV'];

export const campaignTypeDropdownValues: IDropdownValue[] = Object.values(CampaignTypeEnum)
  .filter((val) => typeof val === 'string')
  .map((val) => ({
    label: skipFormattingEnumValues.includes(val)
      ? val.replace(/_/g, ' ')
      : val
        .split('_')
        .map((fragment) => fragment.charAt(0).toLocaleUpperCase() + fragment.slice(1).toLocaleLowerCase())
        .join(' '),
    value: val,
  }));

export const systemNumberTypeDropdownValues: IDropdownValue[] = Object.values(SystemNumberTypeEnum)
  .filter((val) => typeof val === 'string')
  .map((val) => ({
    label: skipFormattingEnumValues.includes(val)
      ? val
      : val
        .split('_')
        .map((fragment) => fragment.charAt(0).toLocaleUpperCase() + fragment.slice(1).toLocaleLowerCase())
        .join(' '),
    value: val,
  }));

export const systemNumberStatusDropdownValues: IDropdownValue[] = Object.values(SystemNumberStatusEnum)
  .filter((val) => typeof val === 'string')
  .map((val) => ({
    label: skipFormattingEnumValues.includes(val)
      ? val
      : val
        .split('_')
        .map((fragment) => fragment.charAt(0).toLocaleUpperCase() + fragment.slice(1).toLocaleLowerCase())
        .join(' '),
    value: val,
  }));

export const userTypeDropdownValues: IDropdownValue[] = Object.values(Auth0Role)
  .filter((val) => typeof val === 'string')
  .map((val) => ({
    label: skipFormattingEnumValues.includes(val)
      ? val
      : val
        .split('_')
        .map((fragment) => fragment.charAt(0).toLocaleUpperCase() + fragment.slice(1).toLocaleLowerCase())
        .join(' '),
    value: val,
  }));

export const messageTypeDropdownValues: IDropdownValue[] = Object.values(MessageTypeEnum)
  .filter((val) => typeof val === 'string' && val !== 'ANY')
  .map((val) => ({
    label: val,
    value: val,
  }));

export const systemNumberMessageTypeDropdownValues: IDropdownValue[] = Object.values(MessageTypeEnum)
  .filter((val) => typeof val === 'string')
  .map((val) => ({
    label: val,
    value: val,
  }));

export const systemNumberDropdownOptions: IFilterDropdownOptions = {
  type: systemNumberTypeDropdownValues,
  campaignType: campaignTypeDropdownValues,
  messageType: systemNumberMessageTypeDropdownValues,
  status: systemNumberStatusDropdownValues,
  clientName: [],
  providerName: [],
};

export const defaultAreaCodeOptions: ISearchRequest = {
  entity: EntityName.AREA_CODES,
  filters: [],
  pagination: {
    skip: 0,
    take: 10,
  },
  sort: {
    fieldName: 'areaCode',
    sortDirection: SearchSortDirectionEnum.ASC,
  },
};